import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";

// Commission Structure will be fetched dynamically from backend
// No more hardcoded rates - commission rates are now configurable per vendor

const API_URL = `${BASE_API_URL}`;

const initialState = {
  vendorWallet: null,
  driverWallets: [],
  totalWalletBalance: 0,
  vendorWalletBalance: 0,
  totalCommissionEarned: 0,
  totalDriverEarnings: 0,
  pendingCommissions: 0,
  transactions: [],
  walletTransactions: [],
  commissionStructure: null,
  commissionSettings: null,
  loading: false,
  loadingCommission: false,
  error: null,
  lastUpdated: null,
};

// Get vendor's overall wallet summary
export const getVendorWalletSummary = createAsyncThunk(
  "vendorWallet/getVendorWalletSummary",
  async (vendorId, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Get wallet summary from the backend endpoint that includes dynamic commission structure
      // Note: wallet/summary already queries drivers internally, no need for separate driver/stats calls
      const walletRes = await axios.get(`${API_URL}/vendor/${vendorId}/wallet/summary`, config);

      if (walletRes.data.status) {
        return {
          ...walletRes.data.data,
          lastUpdated: new Date().toISOString()
        };
      } else {
        throw new Error(walletRes.data.message || "Failed to fetch wallet summary");
      }
    } catch (error) {
      console.error("Error fetching vendor wallet summary:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch wallet summary"
      );
    }
  }
);

// Get vendor's commission transactions
export const getVendorCommissionTransactions = createAsyncThunk(
  "vendorWallet/getVendorCommissionTransactions",
  async (vendorId, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Fetch commission-related transactions for all vendor's drivers
      const response = await axios.get(`${API_URL}/vendor/${vendorId}/commission-transactions`, config);

      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching commission transactions:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch commission transactions"
      );
    }
  }
);

// Get vendor's commission settings
export const getVendorCommissionSettings = createAsyncThunk(
  "vendorWallet/getVendorCommissionSettings",
  async (vendorId, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(`${API_URL}/vendor/${vendorId}/commission/settings`, config);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching vendor commission settings:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch commission settings"
      );
    }
  }
);

// Get vendor's detailed wallet transactions with history
export const getVendorWalletTransactions = createAsyncThunk(
  "vendorWallet/getVendorWalletTransactions",
  async ({ vendorId, page = 1, limit = 20, type = null, startDate = null, endDate = null }, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Build query parameters
      const params = new URLSearchParams({ page, limit });
      if (type) params.append('type', type);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      // Fetch detailed wallet transactions with history
      const response = await axios.get(`${API_URL}/vendor/${vendorId}/wallet/transactions?${params}`, config);

      return response.data.data || { transactions: [], pagination: {}, summary: {} };
    } catch (error) {
      console.error("Error fetching wallet transactions:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch wallet transactions"
      );
    }
  }
);

// Request commission withdrawal
export const requestCommissionWithdrawal = createAsyncThunk(
  "vendorWallet/requestCommissionWithdrawal",
  async ({ vendorId, amount, bankDetails }, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/vendor/${vendorId}/request-withdrawal`,
        { amount, bankDetails },
        config
      );

      return response.data.data;
    } catch (error) {
      console.error("Error requesting withdrawal:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to request withdrawal"
      );
    }
  }
);

// Add money to driver's wallet (vendor action)
export const addMoneyToDriverWallet = createAsyncThunk(
  "vendorWallet/addMoneyToDriverWallet",
  async ({ driverId, amount, description }, thunkAPI) => {
    try {
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        return thunkAPI.rejectWithValue("Vendor not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/vendor/driver/${driverId}/add-money`,
        { amount, description },
        config
      );

      return response.data.data;
    } catch (error) {
      console.error("Error adding money to driver wallet:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to add money to driver wallet"
      );
    }
  }
);

const vendorWalletSlice = createSlice({
  name: "vendorWallet",
  initialState,
  reducers: {
    clearVendorWalletError: (state) => {
      state.error = null;
    },
    resetVendorWallet: (state) => {
      return initialState;
    },
    updateDriverWalletBalance: (state, action) => {
      const { driverId, newBalance } = action.payload;
      const driverWallet = state.driverWallets.find(dw => dw.driverId === driverId);
      if (driverWallet) {
        const oldBalance = driverWallet.walletBalance;
        driverWallet.walletBalance = newBalance;
        
        // Update total wallet balance
        state.totalWalletBalance = state.totalWalletBalance - oldBalance + newBalance;
        
        // Recalculate pending commissions
        state.pendingCommissions = state.driverWallets
          .filter(dw => dw.walletBalance < 0)
          .reduce((sum, dw) => sum + Math.abs(dw.walletBalance), 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Get vendor wallet summary
      .addCase(getVendorWalletSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorWalletSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.driverWallets = action.payload.driverWallets;
        state.totalWalletBalance = action.payload.totalWalletBalance;
        state.vendorWalletBalance = action.payload.vendorWalletBalance || 0;
        state.totalCommissionEarned = action.payload.totalCommissionEarned;
        state.totalDriverEarnings = action.payload.totalDriverEarnings;
        state.pendingCommissions = action.payload.pendingCommissions;
        state.commissionStructure = action.payload.commissionStructure;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(getVendorWalletSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get commission transactions
      .addCase(getVendorCommissionTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVendorCommissionTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(getVendorCommissionTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get vendor commission settings
      .addCase(getVendorCommissionSettings.pending, (state) => {
        state.loadingCommission = true;
      })
      .addCase(getVendorCommissionSettings.fulfilled, (state, action) => {
        state.loadingCommission = false;
        state.commissionSettings = action.payload;
      })
      .addCase(getVendorCommissionSettings.rejected, (state, action) => {
        state.loadingCommission = false;
        state.error = action.payload;
      })

      // Get detailed wallet transactions
      .addCase(getVendorWalletTransactions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getVendorWalletTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.walletTransactions = action.payload.transactions || [];
        // Store pagination and summary info if needed in the future
        state.transactionsPagination = action.payload.pagination;
        state.transactionsSummary = action.payload.summary;
      })
      .addCase(getVendorWalletTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Request commission withdrawal
      .addCase(requestCommissionWithdrawal.pending, (state) => {
        state.loading = true;
      })
      .addCase(requestCommissionWithdrawal.fulfilled, (state, action) => {
        state.loading = false;
        // Add withdrawal request to transactions
        state.transactions.unshift(action.payload);
      })
      .addCase(requestCommissionWithdrawal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add money to driver wallet
      .addCase(addMoneyToDriverWallet.pending, (state) => {
        state.loading = true;
      })
      .addCase(addMoneyToDriverWallet.fulfilled, (state, action) => {
        state.loading = false;
        // Update the specific driver's wallet balance
        const { driverId, newBalance } = action.payload;
        const driverWallet = state.driverWallets.find(dw => dw.driverId === driverId);
        if (driverWallet) {
          driverWallet.walletBalance = newBalance;
        }
      })
      .addCase(addMoneyToDriverWallet.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearVendorWalletError, 
  resetVendorWallet, 
  updateDriverWalletBalance 
} = vendorWalletSlice.actions;

export default vendorWalletSlice.reducer;
